// ============================================================================
// ManagementTab (F-750/751) — hub de l'onglet Gestion, 3 zones
// ============================================================================
// Monte dans /profile/:email?tab=gestion (remplace le placeholder V1). 0 handler
// local (tout delegue aux enfants). M-750 : AUCUNE garde d'autorite ici — le hub
// consomme can.* du serveur pour les affordances ; un refus revient en banniere.
//
// Zone A : 6 tuiles KPI (drill -> modal de detail). Zone B : portrait & tendances.
// Zone C : gestion (responsable, plans, contrats, documents, statut).
//
// PORTEE DTO (note, pas un bug) : le PoC card #1 affichait « Part de la masse
// salariale (equipe) » via buildRealPayrollRows (donnees de TOUTE l'equipe). Le
// hub gestion est scope a UN employe et ne sert pas la masse d'equipe -> cette
// tuile est OMISE (le lien /planification-salariale de L05 aussi). Signale au rapport.

import { useState } from 'react';
import { QueryError, SkeletonCard } from '@gs/ui';
import { useTranslation } from '../../i18n';
import { ApiForbiddenError, ApiServerError } from '@gs/api-client';
import { useManagement, managementErrorCode } from '../../hooks/useManagement';
import { Card, StatCard, DrillCard, Badge } from './ui';
import { buildHubTimeline } from './compute';
import { ClassGrid } from './ClassGrid';
import { RealRateCard } from './RealRateCard';
import { SalaryProgression } from './SalaryProgression';
import { RatePremiumCard, SalaryHistoryDialog, HoursCard, BenefitsCard, BonusCard, EvaluationsCard } from './remuneration-cards';
import { CheckinTrends, CheckinCard, MemberPulseHistory, RaiseHistoryCard, StrengthsCard, ObjectivesCard } from './portrait';
import { ManagerForm, CareerPlansCard, ContractsCard, EmployeeDocumentsSection, InactivationRequestCard } from './management-cards';
import { annualRealPayroll, bonusAmount } from '../../lib/salary/payroll';
import { buildRaiseHistory } from '../../lib/salary/raise-history';
import { employerBenefitsAnnual, overridesSupplement, premiumsSupplement } from '../../lib/salary/special-hours';
import { resolveNiveau, maxEchelonForNiveau } from '../../lib/salary/niveau';
import { gridClassToLib, toRealRates } from './compute';

const CAD0 = new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 });
const HOURLY = new Intl.NumberFormat('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const money = (n: number) => CAD0.format(n);

type CardKey = 'remuneration' | 'horaire' | 'avantages' | 'boni' | 'evaluations' | null;

export function ManagementTab({ email }: { email: string }) {
  const { t } = useTranslation();
  const q = useManagement(email, true);
  const [openCard, setOpenCard] = useState<CardKey>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const onError = (code: string) => setBanner(code);

  if (q.isLoading) return <SkeletonCard />;
  if (q.isError && q.error instanceof ApiForbiddenError) {
    return <Card><p className="text-field text-slate-500 dark:text-slate-400">{t('gest:management.forbidden')}</p></Card>;
  }
  if (q.isError && q.error instanceof ApiServerError && q.error.status === 404) {
    return <Card><p className="text-field text-slate-500 dark:text-slate-400">{t('gest:management.notFound')}</p></Card>;
  }
  if (q.isError) return <QueryError message={t('gest:management.loadError')} onRetry={() => void q.refetch()} />;
  if (!q.data) return null;

  const d = q.data;
  const comp = d.compensation;
  const record = comp.record;
  const year = d.year;

  // ── Calculs du hub (libs pures, cote client) ──
  const hub = buildHubTimeline(comp, year);
  const timeline = hub?.timeline ?? null;
  const cls = hub?.cls ?? null;
  const libClass = cls ? gridClassToLib(cls) : null;
  const niveau = libClass ? resolveNiveau(libClass.niveaux, record?.currentNiveau ?? null) : null;
  const niveauCap = libClass && record ? maxEchelonForNiveau(libClass, record.currentNiveau) : null;
  const atNiveauCap = record != null && niveau != null && niveauCap != null && record.currentEchelon >= niveauCap;

  const realPayrollNow = record && cls
    ? annualRealPayroll({
        year,
        rates: toRealRates(comp.rateHistory),
        weeklyHours: comp.work?.weeklyHours ?? cls.baseHoursPerWeek ?? 40,
        weeksPerYear: cls.weeksPerYear ?? 52,
        hireDate: record.hireDate,
        departureDate: record.departureDate,
      })
    : null;
  const bonusBase = realPayrollNow && !realPayrollNow.unpriced ? realPayrollNow.total : (timeline?.currentAnnual ?? 0);
  const bonusCurrent = comp.bonusList.find((b) => b.year === year) ?? null;
  const bonusRealAmount = bonusAmount(bonusBase, bonusCurrent?.actualPct ?? null);
  const bonusTargetAmount = bonusAmount(bonusBase, bonusCurrent?.targetPct ?? null);

  const specialRate = timeline?.currentHourly ?? record?.currentHourlyRate ?? null;
  const baseWeekly = comp.work?.weeklyHours ?? cls?.baseHoursPerWeek ?? 40;
  const overrides = [...comp.overrides];
  const premiums = [...comp.premiums];
  const overrideLib = overrides.map((o) => ({ id: o.id, start_date: o.startDate, end_date: o.endDate, weekly_hours: o.weeklyHours, reason: o.reason, created_by_email: '', created_at: '' }));
  const premiumLib = premiums.map((p) => ({ id: p.id, start_date: p.startDate, end_date: p.endDate, premium_per_hour: p.premiumPerHour, reason: p.reason, created_by_email: '', created_at: '' }));
  const specialSupp = specialRate != null && overrides.length ? overridesSupplement(overrideLib, baseWeekly, specialRate, year) : 0;
  const premiumSupp = premiums.length ? premiumsSupplement(premiumLib, baseWeekly, overrideLib, year) : 0;
  const adjustedAnnual = timeline?.currentAnnual != null ? timeline.currentAnnual + specialSupp + premiumSupp : null;

  const benefitsTotal = record
    ? employerBenefitsAnnual({ insurance_biweekly: record.insuranceBiweekly, cell_allowance_biweekly: record.cellAllowanceBiweekly, other_allowance_biweekly: record.otherAllowanceBiweekly, has_company_vehicle: record.hasCompanyVehicle, vehicle_cost_biweekly: comp.vehicleCostBiweekly })
    : 0;

  const bonusByYear = new Map<number, number>();
  const bonusProjByYear = new Map<number, number>();
  const salaryByYear = new Map((timeline?.points ?? []).filter((p) => p.annual !== null).map((p) => [p.year, p.annual as number]));
  for (const b of comp.bonusList) {
    const base = b.year === year ? bonusBase : (salaryByYear.get(b.year) ?? null);
    if (base == null) continue;
    const amt = bonusAmount(base, b.actualPct);
    if (amt > 0) bonusByYear.set(b.year, amt);
    const proj = bonusAmount(base, b.targetPct);
    if (proj > 0) bonusProjByYear.set(b.year, proj);
  }
  const avantages = benefitsTotal > 0 ? { year, annual: benefitsTotal } : null;
  const raises = timeline ? buildRaiseHistory(timeline.points, { currentAnnual: adjustedAnnual }) : [];

  const hireYear = record?.hireDate ? Number(record.hireDate.slice(0, 4)) : null;
  const seniority = hireYear != null && Number.isFinite(hireYear) ? Math.max(0, year - hireYear) : null;
  const currentQuarter = Math.floor(new Date().getMonth() / 3) + 1;
  const annualValue = adjustedAnnual != null ? money(adjustedAnnual) : '—';

  return (
    <div className="flex flex-col gap-8">
      {banner ? (
        <div role="alert" className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-field text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
          <span>{t(`gest:management.errors.${banner}`, t('gest:management.errors.server'))}</span>
          <button type="button" onClick={() => setBanner(null)} className="text-chip font-medium underline">{t('gest:management.common.dismiss')}</button>
        </div>
      ) : null}

      {/* ZONE A — KPI */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <DrillCard
          eyebrow={t('gest:management.kpi.remuneration')}
          value={<>{annualValue}{specialRate != null && adjustedAnnual != null ? <span className="text-field font-normal text-slate-500 dark:text-slate-400"> · {HOURLY.format(specialRate)} $/h</span> : null}</>}
          hint={record && cls ? `${cls.label} · ${niveau ? `N${niveau.level}` : t('gest:management.class.niveauUndefinedShort')} · ${record.offGrid ? t('gest:management.class.offGridShort') : t('gest:management.class.echelonOf', { echelon: record.currentEchelon, total: niveauCap ?? cls.echelons.length })}` : t('gest:management.kpi.notConfigured')}
          title={t('gest:management.kpi.remunerationTitle')}
          size="xl"
          open={openCard === 'remuneration'}
          onToggle={(o) => setOpenCard(o ? 'remuneration' : null)}
        >
          <div className="flex flex-col gap-4">
            <ClassGrid email={email} record={record} classes={comp.classes} onError={onError} action={<SalaryHistoryDialog email={email} entries={comp.salaryHistory} currentYear={year} onError={onError} />} />
            {d.can.setHourlyRate ? <RealRateCard email={email} rateHistory={comp.rateHistory} offGrid={record?.offGrid ?? false} onError={onError} /> : null}
            <RatePremiumCard email={email} premiums={premiums} baseWeeklyHours={baseWeekly} overrides={overrides} year={year} onError={onError} />
            {timeline ? <SalaryProgression timeline={timeline} bonusByYear={bonusByYear} bonusProjByYear={bonusProjByYear} avantages={avantages} augGrillePct={cls?.augGrillePct ?? null} /> : null}
          </div>
        </DrillCard>

        <DrillCard
          eyebrow={t('gest:management.kpi.hours')}
          value={comp.work?.weeklyHours != null ? t('gest:management.kpi.hoursValue', { hours: comp.work.weeklyHours }) : '—'}
          hint={overrides.length > 0 ? t('gest:management.kpi.hoursPeriods', { count: overrides.length }) : t('gest:management.kpi.hoursStandard')}
          title={t('gest:management.kpi.hoursTitle')}
          open={openCard === 'horaire'}
          onToggle={(o) => setOpenCard(o ? 'horaire' : null)}
        >
          <HoursCard email={email} comp={comp} currentYear={year} specialRate={specialRate} onError={onError} />
        </DrillCard>

        <DrillCard
          eyebrow={t('gest:management.kpi.benefits')}
          value={benefitsTotal > 0 ? t('gest:management.kpi.perYear', { amount: money(benefitsTotal) }) : '—'}
          hint={record?.hasCompanyVehicle ? t('gest:management.kpi.benefitsVehicle') : t('gest:management.kpi.benefitsList')}
          title={t('gest:management.kpi.benefitsTitle')}
          open={openCard === 'avantages'}
          onToggle={(o) => setOpenCard(o ? 'avantages' : null)}
        >
          <BenefitsCard email={email} comp={comp} currentYear={year} onError={onError} />
        </DrillCard>

        <DrillCard
          eyebrow={t('gest:management.kpi.bonus')}
          value={bonusRealAmount > 0 ? t('gest:management.kpi.perYear', { amount: money(bonusRealAmount) }) : '—'}
          hint={bonusCurrent ? t('gest:management.kpi.bonusTarget', { amount: money(bonusTargetAmount) }) : t('gest:management.kpi.bonusUnset')}
          title={t('gest:management.kpi.bonusTitle')}
          open={openCard === 'boni'}
          onToggle={(o) => setOpenCard(o ? 'boni' : null)}
        >
          <BonusCard email={email} bonusList={comp.bonusList} bonusBase={bonusBase} currentYear={year} onError={onError} />
        </DrillCard>

        <DrillCard
          eyebrow={t('gest:management.kpi.eval')}
          value={d.evaluations[0] ? `${d.evaluations[0].score}${d.evalConfig ? `/${d.evalConfig.maxScore}` : ''}` : '—'}
          hint={d.evaluations[0] ? String(d.evaluations[0].year) : t('gest:management.kpi.evalNone')}
          title={t('gest:management.kpi.evalTitle')}
          open={openCard === 'evaluations'}
          onToggle={(o) => setOpenCard(o ? 'evaluations' : null)}
        >
          <EvaluationsCard email={email} evaluations={d.evaluations} evalConfig={d.evalConfig} canWrite={d.can.writeEvaluation} currentYear={year} onError={onError} />
        </DrillCard>

        <StatCard label={t('gest:management.kpi.seniority')} value={seniority != null ? t('gest:management.kpi.years', { count: seniority }) : '—'} hint={record?.hireDate ? t('gest:management.kpi.since', { date: record.hireDate.slice(0, 10) }) : t('gest:management.kpi.dateUnknown')} />
      </div>

      {/* ZONE B — Portrait & tendances */}
      <section className="flex flex-col gap-4">
        <div>
          <h2 className="text-body font-semibold text-slate-800 dark:text-slate-100">{t('gest:management.zoneB.title')}</h2>
          <p className="text-meta text-slate-500 dark:text-slate-400">{t('gest:management.zoneB.description')}</p>
        </div>
        <div className="grid items-start gap-4 md:grid-cols-2">
          <CheckinTrends checkins={d.checkins} />
          <RaiseHistoryCard rows={raises} />
          <CheckinCard email={email} year={year} currentQuarter={currentQuarter} checkins={d.checkins} onError={onError} />
          <MemberPulseHistory pulses={d.pulses} />
          <StrengthsCard email={email} strengths={d.strengths} onError={onError} />
          <ObjectivesCard email={email} year={year} objectives={d.objectives} onError={onError} />
        </div>
      </section>

      {/* ZONE C — Gestion */}
      <section className="flex flex-col gap-4">
        <div>
          <h2 className="text-body font-semibold text-slate-800 dark:text-slate-100">{t('gest:management.zoneC.title')}</h2>
          <p className="text-meta text-slate-500 dark:text-slate-400">{t('gest:management.zoneC.description')}</p>
        </div>
        <div className="grid items-start gap-4 md:grid-cols-2">
          {d.target.teamId ? <ManagerForm email={email} target={d.target} options={d.rosterOptions} onError={onError} /> : null}
          <CareerPlansCard email={email} plans={d.plans} onError={onError} />
          <ContractsCard email={email} contracts={comp.contracts} templates={d.templates} onError={onError} />
          <EmployeeDocumentsSection
            email={email}
            documents={d.documents}
            canManage={d.can.manageDocuments}
            onError={onError}
          />
        </div>
        <InactivationRequestCard email={email} target={d.target} pending={d.pendingInactivation} canRequest={d.can.requestInactivation} onError={onError} />
      </section>
    </div>
  );
}
